import {
  Box,
  Button,
  Container,
  Grid,
  Header,
  Input,
  SpaceBetween,
  Spinner
} from "@cloudscape-design/components";
import { useEffect, useMemo, useState } from "react";

import { faArrowUpFromBracket } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ExtensionObjectType } from "types/extensionTypes";
import UploadExtensionModal from "./UploadExtensionModal";
import deploymentService from "services/deploymentService";
import { useCluster } from "contexts/ClusterContext";
import ExtensionCard from "./ExtensionCard";
import { useNotification } from "contexts/NotificationContext";
import DeleteConfirmationModal from "components/shared/DeleteConfirmationModal";
import ActionConfirmationModal from "components/shared/ActionConfirmationModal";




function CustomExtensionsTab({ refreshKey }: { refreshKey: number }) {
  const [customExtensionsList, setCustomExtensionsList] = useState<ExtensionObjectType[]>([]);
  const [loadingCustomExtensions, setLoadingCustomExtensions] = useState(true);
  const [errorLoadingCustomExtensions, setErrorLoadingCustomExtensions] = useState<string | null>(null);

  const [extensionSearch, setExtensionSearch] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);

  const { selectedCluster } = useCluster();

  const [loadingUninstallation, setLoadingUninstallation] = useState(false);

  const { addNotification } = useNotification();

  const [showUninstallModal, setShowUninstallModal] = useState(false);

  const [selectedExtension, setSelectedExtension] = useState<ExtensionObjectType | null>(null);

  const [loadingUplodExtension, setLoadingUploadExtension] = useState(false);

  const [showInsatllConfirmationModal, setShowInstallConfirmationModal] = useState(false);

  const [loadingInstallation, setLoadingInstallation] = useState(false);

  

  const fetchCustomExtensions = async () => {
    try {
      setLoadingCustomExtensions(true);
      const response = await deploymentService.getAllExtensions(selectedCluster?.value, "custom", "all");
      setCustomExtensionsList(response);
    } catch (err) {
      setErrorLoadingCustomExtensions("Failed to load custom extensions.");
      console.error(err);
    } finally {
      setLoadingCustomExtensions(false);
    }
  };

  useEffect(() => {
    fetchCustomExtensions();
  }, [selectedCluster?.value, refreshKey]);



  const filteredCustomExtensions = useMemo(() => {
    return customExtensionsList.filter((extension) =>
      extension.displayName
        .toLowerCase()
        .includes(extensionSearch.trim().toLowerCase())
    );
  }, [customExtensionsList, extensionSearch]);




  const installCustomExtension = async(extension: ExtensionObjectType, parameters:Record<string, string|boolean> | null)=>{
    try {      
      setLoadingInstallation(true);

      await deploymentService.installExtension(selectedCluster?.value, extension.id, parameters);
      
      fetchCustomExtensions();
     
      addNotification("success", `${extension.displayName} installation has started.`);
      
    } catch (error) {
      console.error(`Failed to Install ${extension.displayName}`, error);
      addNotification("error", `Failed to install ${extension.displayName}.`);
      // Rethrow so callers (confirmation modal) can keep the modal open and surface the error instead of automatically closing it.
      throw error;

    } finally{
      setLoadingInstallation(false);
    }
  }


  const uninstallCustomExtension = async(extension: ExtensionObjectType)=>{
    setLoadingUninstallation(true);
    try {
      
      await deploymentService.uninstallExtension(selectedCluster?.value, extension.id);
      
      fetchCustomExtensions();
      addNotification("success", `${extension.displayName} uninstallation has started.`);

    } catch (error) {
      console.error(`Failed to Uninstall ${extension.displayName}`, error);
      addNotification("error", `Failed to uninstall ${extension.displayName}.`);

    } finally{
      setLoadingUninstallation(false);
    }
  }



  const openInstallModal = (extension: ExtensionObjectType)=>{
    setSelectedExtension(extension);
    setShowInstallConfirmationModal(true);
  }


  const openUninstallModal = (extension: ExtensionObjectType)=>{
    setSelectedExtension(extension);
    setShowUninstallModal(true);
  }

  

  const handleUploadExtension = async ({
    file,
    extensionName,
    extensionDescription,
    supportedVersions
  }: {
    file: File;
    extensionName: string;
    extensionDescription: string;
    supportedVersions: string[];
  }) => {
    try {
      setLoadingUploadExtension(true);

      const formData = new FormData();

      formData.append("file", file);
      formData.append("extensionName", extensionName);
      formData.append("extensionDescription", extensionDescription);
      formData.append(
        "supportedVersions",
        JSON.stringify(supportedVersions)
      );

      await deploymentService.uploadExtension(formData, "custom");

      fetchCustomExtensions();

      addNotification("success", `${extensionName} uploaded successfuly.`);

    } catch (error) {
        console.error(error);
        addNotification("error", `Failed to upload ${extensionName}.`);
        throw error;
    } finally{
      setLoadingUploadExtension(false);
    }
  }




  // 1. LOADING STATE
  if (loadingCustomExtensions) {
    return (
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <SpaceBetween size="m">
          <Spinner size="large" />
          <Box color="text-body-secondary">Loading custom extensions...</Box>
        </SpaceBetween>
      </Box>
    );
  }

  // 2. ERROR STATE
  if (errorLoadingCustomExtensions) {
    return (
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <SpaceBetween size="m">
          <Box variant="h3" color="text-status-error">{errorLoadingCustomExtensions}</Box>
          <Button onClick={fetchCustomExtensions} iconName="refresh">Retry</Button>
        </SpaceBetween>
      </Box>
    );
  }  

  // 3. EMPTY STATE
  if (customExtensionsList.length === 0){
    return (
      <SpaceBetween size="l">
        <Container>
          <Box textAlign="center" padding={{ vertical: "xxl" }}>
            <SpaceBetween size="m">
              <Box color="text-body-secondary">
                <FontAwesomeIcon
                  icon={faArrowUpFromBracket}
                  className="margin-right-10"
                  style={{fontSize:"35px"}}
                />
              </Box>
              
              <div>
                <Box variant="h3">No custom extensions yet</Box>
                <Box variant="p" color="text-body-secondary">
                  Upload your first custom Keycloak extension to get started.
                </Box>
              </div>

              <Button variant="primary" onClick={()=>setShowUploadModal(true)}>
                <FontAwesomeIcon
                  icon={faArrowUpFromBracket}
                  className="margin-right-10"
                />
                Upload Extension
              </Button>
            </SpaceBetween>
          </Box>
        </Container>

        <UploadExtensionModal 
          visible={showUploadModal} 
          isLoadingUpload={loadingUplodExtension}
          onSubmit={handleUploadExtension}
          onDismiss={()=>{setShowUploadModal(false);}} 
        />
      </SpaceBetween>
    )
  }

  // 4. MAIN TAB CONTENT
  return (
    <SpaceBetween size="l">
      {/* Tab Header with Upload Action */}
      <Header
        variant="h2"
        description="Upload and manage your custom Keycloak extensions"
        actions={
          <Button variant="primary" onClick={()=>setShowUploadModal(true)}>
            <FontAwesomeIcon
              icon={faArrowUpFromBracket}
              className="margin-right-10"
            />
            Upload Extension
          </Button>
        }
      >
        Custom Extensions
      </Header>

      <SpaceBetween size="s" direction="horizontal">
        <Input 
          value={extensionSearch} 
          onChange={({detail})=>setExtensionSearch(detail.value)} 
          type="search" 
          placeholder="Search extensions..." 
          className="extensions-search-input"
        />
      </SpaceBetween>

      {/* Custom Extensions Grid or Empty State Container */}
      {filteredCustomExtensions.length === 0 ? (
        <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <div>
              <b>No extensions found</b>
              <Box variant="p" color="text-body-secondary">
                We couldn't find any extensions matching your search. Try adjusting your search term.
              </Box>
            </div>

            <Button onClick={() => setExtensionSearch("")}>
              Clear search
            </Button>
          </SpaceBetween>
        </Box>
        ) : (
        <Grid
          gridDefinition={filteredCustomExtensions.map(()=>({ colspan: { default: 12, s: 4, m: 3, l: 3 } }))}
        >
          {filteredCustomExtensions.map((extension, index)=> 
            <ExtensionCard 
              key={index} 
              extension={extension} 
              viewMode="custom-tab" 
              onInstall={openInstallModal} 
              onUninstall={openUninstallModal}
            />
          )}    
        </Grid>
      )}

      <UploadExtensionModal 
        visible={showUploadModal} 
        isLoadingUpload={loadingUplodExtension}
        onSubmit={handleUploadExtension}
        onDismiss={()=>{
          setShowUploadModal(false);
        }} 
      />



      <ActionConfirmationModal
        isVisible={showInsatllConfirmationModal}
        isLoading={loadingInstallation}
        onDismiss={() => {
          // Close confirmation modal; clear selection
          setShowInstallConfirmationModal(false);
          setSelectedExtension(null);
        }}
        onConfirm={async () => {
          if (!selectedExtension) return;

          await installCustomExtension(selectedExtension, null);

          // On success, close the confirmation modal and clear selection
          setShowInstallConfirmationModal(false);
          setSelectedExtension(null);
        }}
      >
        Are you sure you want to install extension: <b>{selectedExtension?.displayName}</b> on <b>{selectedCluster?.label}</b>?
      </ActionConfirmationModal>




      <DeleteConfirmationModal 
        isVisible={showUninstallModal} 
        isDeleting={loadingUninstallation} 
        onDismiss={()=>{
          setSelectedExtension(null);
          setShowUninstallModal(false);
        }} 
        onConfirm={async ()=>{
          if(!selectedExtension) return;

          await uninstallCustomExtension(selectedExtension);

          setShowUninstallModal(false);
          setSelectedExtension(null);
        }} 
        deleteConfirmationValue={"uninstall extension"} 
        deleteAction = "uninstallation"
        headerText = "Uninstall Extension"
        headerIconName = "status-stopped"
        mainButtonText = "Uninstall"
        mainButtonLoadingText = "Uninstalling..."
      >
        Are you sure you want to uninstall extension: <b>{selectedExtension?.displayName}</b> from <b>{selectedCluster?.label}</b> ?
      </DeleteConfirmationModal>
    </SpaceBetween>
  );
}

export default CustomExtensionsTab;